import {
  AddCircleOutline,
  ExpandMore,
  FilterAltOutlined,
  NotificationsActiveOutlined,
  PhotoLibraryOutlined,
  ScoreboardOutlined,
  SdCardOutlined,
  VideocamOutlined,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import IconButton from "../../components/ui/IconButton";
import LoadingButton from "../../components/ui/LoadingButton";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CameraStatusTable, {
  CameraStatus,
} from "../../components/Dashboard/CameraStatusTable";
import DatePickerField from "../../components/Dashboard/DatePickerField";
import MetricChartCard from "../../components/Dashboard/MetricChartCard";
import ConfirmDialog from "../../components/ConfirmDialog";
import StatusPill from "../../components/ui/StatusPill";
import { AuthContext } from "../../contexts/AuthContext";
import { useCountry } from "../../contexts/CountryContext";
import { useIntl } from "../../contexts/IntlContext";
import { PageHeader } from "../../components/PageHeader";
import api from "../../services/api";
import { fetchCameraStatusShared } from "../../services/cameraStatus";
import {
  fetchEstablishmentsForFilters,
  searchEstablishmentsForFilters,
} from "../../services/establishmentsResolver";
import { useEstablishmentSearch } from "../../hooks/useEstablishmentSearch";
import { useLocationFilterOptions } from "../../hooks/useLocationFilterOptions";
import {
  getYoutubeOwnerConnectionStatus,
  setPendingYoutubeOAuthPopup,
  YoutubeOwnerConnectionStatus,
} from "../../services/youtubeOwnerConnection";
import { EUserRole } from "../../types/enums";
import { hasOperationalAccess } from "../../utils/authRole";
import { formatLiveStatus, getLiveStatusLevel } from "../../utils/liveStatus";
import Select from "../../components/ui/Select";
import Autocomplete from "../../components/ui/Autocomplete";
import Input from "../../components/ui/Input";
import { fontWeights } from "../../themes/fontWeights";

type TOwnerLiveRow = {
  liveId: number;
  name: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  paymentPending?: boolean;
};

type TCameraStatusApiRow = {
  establishmentId: string;
  establishmentName: string | null;
  deviceId: number;
  deviceName: string;
  channelUuid?: string;
  channelName?: string;
  online: boolean;
  lastEventDateTime?: string;
  lastEventFormatted?: string;
  lastCommunicationSuccessDateTime?: string;
  lastCommunicationCheckDateTime?: string;
  lastOfflineReason?: string;
  errorReasons?: string;
  buttonStatus?:
    | "OK"
    | "OFFLINE"
    | "NOT_CONFIGURED"
    | "NOT_APPLICABLE"
    | string;
  buttonStatusLevel?: "ok" | "alert" | string;
  sdStatus?: "OK" | "ERROR" | "UNKNOWN" | string;
  sdCapacityMb?: number;
  sdFreeSpaceMb?: number;
  sdRawStatus?: string;
  buttonHealthStatus?: "OK" | "BAD" | "UNKNOWN" | "NOT_APPLICABLE" | string;
  buttonHealthReason?: "VERY_LONG_VIDEO" | "THREE_LONG_VIDEOS" | string;
};

type TCameraStatusApiResponse = {
  data: TCameraStatusApiRow[];
  errors: Array<{ establishmentId: string; message: string }>;
};

type TEstablishmentOption = {
  id: string;
  name: string;
};

type TChannelOption = {
  id: string;
  name: string;
};

// Filtragem feita no servidor: o Autocomplete não deve refiltrar as opções localmente.
const serverSideFilter = <T,>(options: T[]) => options;

const Dashboard = () => {
  const intl = useIntl();
  const { country } = useCountry();
  const { authInfo } = useContext(AuthContext);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const ALL_ESTABLISHMENTS = "__all__";
  const ALL_COURTS = "__all__";
  const [cameraRows, setCameraRows] = useState<TCameraStatusApiRow[]>([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [selectedEstablishmentId, setSelectedEstablishmentId] =
    useState<string>("");
  const [chartsEstablishmentId, setChartsEstablishmentId] =
    useState<string>(ALL_ESTABLISHMENTS);
  const [chartsCourtId, setChartsCourtId] = useState<string>(ALL_COURTS);
  const [chartViewMode, setChartViewMode] = useState<"chart" | "table">("table");
  const [channelsByEstablishment, setChannelsByEstablishment] = useState<
    Map<string, TChannelOption[]>
  >(new Map());
  const [establishmentsFromApi, setEstablishmentsFromApi] = useState<
    TEstablishmentOption[]
  >([]);
  const [establishmentsLoading, setEstablishmentsLoading] = useState(false);
  const [chartsStates, setChartsStates] = useState<string[]>([]);
  const [chartsCities, setChartsCities] = useState<string[]>([]);

  const [endDate, setEndDate] = useState(() =>
    new Date().toLocaleDateString("sv-SE"),
  );
  const [startDate, setStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return start.toLocaleDateString("sv-SE");
  });
  const todayISO = () => new Date().toLocaleDateString("sv-SE");
  const last7DaysISO = () => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return start.toLocaleDateString("sv-SE");
  };

  const generateSerieLabel = (item: any) =>
    item.channelId
      ? `${item.establishmentName} - ${item.channelName}`
      : "Total";
  const canOperate = hasOperationalAccess(authInfo?.role);
  const isOwner = authInfo?.role === EUserRole.OWNER;
  const navigate = useNavigate();

  const [ownerStartedLives, setOwnerStartedLives] = useState<TOwnerLiveRow[]>([]);
  const [ownerRecentLives, setOwnerRecentLives] = useState<TOwnerLiveRow[]>([]);
  const [ownerLivesLoading, setOwnerLivesLoading] = useState(false);
  const [youtubeStatus, setYoutubeStatus] = useState<YoutubeOwnerConnectionStatus | null>(null);

  useEffect(() => {
    if (!isOwner) return;
    let canceled = false;

    const loadOwnerSummary = async () => {
      setOwnerLivesLoading(true);
      try {
        const [startedRes, privatesRes, youtube] = await Promise.all([
          api.get("/app/lives/started", { params: { page: 0, limit: 5 } }),
          api.get("/app/lives/privates/paginated", { params: { page: 0, limit: 5 } }),
          getYoutubeOwnerConnectionStatus().catch(() => null),
        ]);
        if (canceled) return;
        setOwnerStartedLives(Array.isArray(startedRes.data?.data) ? startedRes.data.data : []);
        setOwnerRecentLives(Array.isArray(privatesRes.data?.data) ? privatesRes.data.data : []);
        setYoutubeStatus(youtube);
      } catch {
        if (!canceled) {
          setOwnerStartedLives([]);
          setOwnerRecentLives([]);
        }
      } finally {
        if (!canceled) setOwnerLivesLoading(false);
      }
    };

    loadOwnerSummary();
    return () => {
      canceled = true;
    };
  }, [isOwner]);

  // Analytics logic aligned with Reporter (/reporter): same API, same params.
  // Scope differs by role (owner = own establishments, admin = all).
  // Presentation simplified: compact table, fixed daily granularity.
  const borderColor = theme.palette.divider;
  const chartSurfaceStyles = useMemo(
    () => (isDarkMode ? undefined : { border: `1px solid ${borderColor}` }),
    [borderColor, isDarkMode],
  );
  const filterControlSx = useMemo(
    () => ({
      minWidth: { xs: 0, sm: 280 },
      width: { xs: "100%", sm: "auto" },
    }),
    [],
  );
  // Ensure the select menu stays above charts/canvas overlays.
  const selectMenuSx = useMemo(
    () => ({ zIndex: theme.zIndex.modal + 1000 }),
    [theme.zIndex.modal],
  );

  const unknownLabel = useMemo(
    () => intl.formatMessage({ id: "dashboard.statusUnknown" }),
    [intl],
  );

  const [ownerMetricsExpanded, setOwnerMetricsExpanded] = useState(false);
  const [formattingSdByDevice, setFormattingSdByDevice] = useState<
    Record<string, boolean>
  >({});
  const [sdFormatTarget, setSdFormatTarget] = useState<{
    establishmentId: string;
    deviceId: number;
  } | null>(null);

  const refreshCameraStatus = useCallback(
    async (force = false, establishmentIds?: string[]) => {
      const normalizedEstablishmentIds = Array.from(
        new Set(
          (establishmentIds ?? [])
            .map((id) => String(id ?? "").trim())
            .filter(Boolean),
        ),
      );

      if (
        hasOperationalAccess(authInfo?.role) &&
        normalizedEstablishmentIds.length === 0 &&
        selectedEstablishmentId &&
        selectedEstablishmentId !== ALL_ESTABLISHMENTS
      ) {
        normalizedEstablishmentIds.push(selectedEstablishmentId);
      }

      setCameraLoading(true);
      setCameraError(null);
      try {
        const response = (await fetchCameraStatusShared({
          force,
          establishmentIds: normalizedEstablishmentIds,
        })) as TCameraStatusApiResponse;
        setCameraRows(Array.isArray(response?.data) ? response.data : []);
        if (
          Array.isArray(response?.errors) &&
          response.errors.length > 0
        ) {
          setCameraError(
            intl.formatMessage(
              { id: "dashboard.cameraStatusPartialError" },
              { count: response.errors.length },
            ),
          );
        }
      } catch {
        setCameraRows([]);
        setCameraError(intl.formatMessage({ id: "dashboard.cameraStatusError" }));
      } finally {
        setCameraLoading(false);
      }
    },
    [ALL_ESTABLISHMENTS, authInfo?.role, intl, selectedEstablishmentId],
  );

  const handleFormatSd = useCallback(
    async (establishmentId: string, deviceId: number) => {
      const key = `${establishmentId}:${deviceId}`;
      setFormattingSdByDevice((prev) => ({ ...prev, [key]: true }));

      try {
        await api.post(
          `/app/cameras/${encodeURIComponent(establishmentId)}/devices/${deviceId}/storage/format`,
        );
        toast.success(intl.formatMessage({ id: "dashboard.sdFormatSuccess" }));
        const establishmentIds =
          selectedEstablishmentId &&
          selectedEstablishmentId !== ALL_ESTABLISHMENTS
            ? [selectedEstablishmentId]
            : [establishmentId];
        await refreshCameraStatus(true, establishmentIds);
      } catch (e: any) {
        const msg =
          typeof e?.response?.data?.message === "string"
            ? e.response.data.message
            : intl.formatMessage({ id: "dashboard.sdFormatError" });
        toast.error(msg);
      } finally {
        setFormattingSdByDevice((prev) => ({ ...prev, [key]: false }));
      }
    },
    [ALL_ESTABLISHMENTS, intl, refreshCameraStatus, selectedEstablishmentId],
  );

  const establishments = useMemo(() => {
    if (establishmentsFromApi.length > 0) {
      return establishmentsFromApi;
    }

    const map = new Map<string, string>();

    for (const row of cameraRows) {
      if (!row.establishmentId) continue;
      map.set(
        row.establishmentId,
        row.establishmentName?.trim() || row.establishmentId,
      );
    }

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
  }, [cameraRows, establishmentsFromApi]);

  const mergeEstablishmentsFromSearch = useCallback(
    (rows: { id: string; name: string }[]) => {
      setEstablishmentsFromApi((prev) => {
        const known = new Set(prev.map((option) => option.id));
        const added = rows.filter((row) => !known.has(row.id));
        if (!added.length) return prev;
        return [
          ...prev,
          ...added.map((row) => ({ id: row.id, name: row.name })),
        ];
      });
    },
    [],
  );
  const camerasEstablishmentSearch = useEstablishmentSearch({
    country,
    enabled: !isOwner,
    onResults: mergeEstablishmentsFromSearch,
  });
  const chartsEstablishmentSearch = useEstablishmentSearch({
    country,
    enabled: !isOwner,
    onResults: mergeEstablishmentsFromSearch,
  });
  const locationFilters = useLocationFilterOptions({
    country,
    enabled: canOperate,
    states: chartsStates,
  });

  // Referência estável: o MUI zera o texto digitado quando a identidade do `value` muda.
  const allEstablishmentsOption = useMemo(
    () => ({
      id: ALL_ESTABLISHMENTS,
      name: intl.formatMessage({ id: "analyticsV2.all" }),
    }),
    [ALL_ESTABLISHMENTS, intl],
  );
  const establishmentsWithAllOption = useMemo(
    () => [allEstablishmentsOption, ...establishments],
    [allEstablishmentsOption, establishments],
  );
  const isOwnerSingleEstablishment =
    isOwner && establishments.length === 1;
  const establishmentsFilterOptions = useMemo(
    () =>
      isOwnerSingleEstablishment
        ? establishments
        : establishmentsWithAllOption,
    [establishments, establishmentsWithAllOption, isOwnerSingleEstablishment],
  );

  // Opções do Autocomplete de estabelecimento: resultado da busca server-side + "Todos" + o item
  // já selecionado (para o value continuar casando com uma opção após nova busca).
  const buildSearchOptions = useCallback(
    (found: { id: string; name: string }[], selectedId: string) => {
      // Reaproveita o objeto já cacheado de cada id: nova busca não pode trocar a identidade do
      // item selecionado, senão o MUI reseta o texto digitado.
      const cached = new Map(establishments.map((option) => [option.id, option]));
      const options = found.map((option) => cached.get(option.id) ?? option);
      const selected = cached.get(selectedId);
      if (selected && !options.some((option) => option.id === selected.id)) {
        options.unshift(selected);
      }
      return [allEstablishmentsOption, ...options];
    },
    [allEstablishmentsOption, establishments],
  );
  const camerasEstablishmentOptions = useMemo(
    () =>
      isOwner
        ? establishmentsFilterOptions
        : buildSearchOptions(
            camerasEstablishmentSearch.options,
            selectedEstablishmentId,
          ),
    [
      buildSearchOptions,
      camerasEstablishmentSearch.options,
      establishmentsFilterOptions,
      isOwner,
      selectedEstablishmentId,
    ],
  );
  const chartsEstablishmentOptions = useMemo(
    () =>
      isOwner
        ? establishmentsFilterOptions
        : buildSearchOptions(
            chartsEstablishmentSearch.options,
            chartsEstablishmentId,
          ),
    [
      buildSearchOptions,
      chartsEstablishmentSearch.options,
      chartsEstablishmentId,
      establishmentsFilterOptions,
      isOwner,
    ],
  );

  const formatLastEvent = useCallback(
    (row: TCameraStatusApiRow) => {
      if (row.lastEventDateTime) {
        // A API já devolve a data pronta no fuso do estabelecimento; se o fuso for
        // desconhecido ela vem vazia e caímos para o fuso do navegador.
        const date = new Date(row.lastEventDateTime);
        return {
          label:
            row.lastEventFormatted ||
            (Number.isNaN(date.getTime())
              ? row.lastEventDateTime
              : date.toLocaleString(intl.locale)),
          level: row.online ? ("ok" as const) : ("alert" as const),
        };
      }
      return {
        label: row.lastOfflineReason ? row.lastOfflineReason : unknownLabel,
        level: row.online ? ("ok" as const) : ("alert" as const),
      };
    },
    [intl.locale, unknownLabel],
  );

  useEffect(() => {
    if (selectedEstablishmentId) return;
    if (!establishments.length) return;
    setSelectedEstablishmentId(establishments[0].id);
  }, [establishments, selectedEstablishmentId]);

  useEffect(() => {
    if (!isOwnerSingleEstablishment) return;
    const onlyId = establishments[0]?.id;
    if (!onlyId) return;
    if (selectedEstablishmentId !== onlyId) {
      setSelectedEstablishmentId(onlyId);
    }
    if (chartsEstablishmentId !== onlyId) {
      setChartsEstablishmentId(onlyId);
    }
  }, [
    chartsEstablishmentId,
    establishments,
    isOwnerSingleEstablishment,
    selectedEstablishmentId,
  ]);

  useEffect(() => {
    if (!establishments.length) return;
    if (chartsEstablishmentId === ALL_ESTABLISHMENTS) {
      if (isOwnerSingleEstablishment) {
        setChartsEstablishmentId(establishments[0].id);
      }
      return;
    }
    if (establishments.some((e) => e.id === chartsEstablishmentId)) return;
    if (isOwnerSingleEstablishment) {
      setChartsEstablishmentId(establishments[0].id);
      return;
    }
    setChartsEstablishmentId(ALL_ESTABLISHMENTS);
  }, [
    ALL_ESTABLISHMENTS,
    chartsEstablishmentId,
    establishments,
    isOwnerSingleEstablishment,
  ]);

  const chartEstablishmentIds = useMemo(() => {
    if (chartsEstablishmentId && chartsEstablishmentId !== ALL_ESTABLISHMENTS) {
      return [chartsEstablishmentId];
    }
    return undefined;
  }, [ALL_ESTABLISHMENTS, chartsEstablishmentId]);

  const availableCourts = useMemo(() => {
    if (chartsEstablishmentId === ALL_ESTABLISHMENTS) return [];
    return channelsByEstablishment.get(chartsEstablishmentId) ?? [];
  }, [ALL_ESTABLISHMENTS, chartsEstablishmentId, channelsByEstablishment]);

  const chartChannelIds = useMemo(() => {
    if (chartsEstablishmentId === ALL_ESTABLISHMENTS) return undefined;
    if (chartsCourtId && chartsCourtId !== ALL_COURTS) return [chartsCourtId];
    return undefined;
  }, [ALL_COURTS, ALL_ESTABLISHMENTS, chartsCourtId, chartsEstablishmentId]);

  const chartCities = useMemo(
    () => (chartsCities.length ? chartsCities : undefined),
    [chartsCities],
  );
  const chartStatesFilter = useMemo(
    () => (chartsStates.length ? chartsStates : undefined),
    [chartsStates],
  );

  const chartRefreshKey = useMemo(
    () =>
      [
        startDate,
        endDate,
        chartsEstablishmentId,
        chartsCourtId,
        (chartEstablishmentIds ?? []).join(","),
        (chartChannelIds ?? []).join(","),
        (chartCities ?? []).join(","),
        (chartStatesFilter ?? []).join(","),
      ].join("|"),
    [
      chartChannelIds,
      chartCities,
      chartStatesFilter,
      chartEstablishmentIds,
      chartsCourtId,
      chartsEstablishmentId,
      endDate,
      startDate,
    ],
  );

  const cameras = useMemo(() => {
    const filteredRows =
      selectedEstablishmentId && selectedEstablishmentId !== ALL_ESTABLISHMENTS
        ? cameraRows.filter(
            (row) => row.establishmentId === selectedEstablishmentId,
          )
        : cameraRows;

    return filteredRows.map((row) => {
      const parts = [
        row.establishmentName,
        row.channelName,
        row.deviceName,
      ].filter(Boolean);
      const displayName = parts.length ? parts.join(" - ") : row.deviceName;

      const online = !!row.online;
      const lastEvent = formatLastEvent({ ...row, online });

      const buttonHealthIsBad = row.buttonHealthStatus === "BAD";
      const buttonHealthTooltip = buttonHealthIsBad
        ? (() => {
            const reason = (() => {
              switch (row.buttonHealthReason) {
                case "VERY_LONG_VIDEO":
                  return intl.formatMessage({
                    id: "dashboard.buttonHealthReasonVeryLong",
                  });
                case "THREE_LONG_VIDEOS":
                  return intl.formatMessage({
                    id: "dashboard.buttonHealthReasonThreeLong",
                  });
                default:
                  return intl.formatMessage({ id: "dashboard.statusUnknown" });
              }
            })();
            return `${intl.formatMessage({ id: "dashboard.buttonHealthBadTitle" })}: ${reason}`;
          })()
        : intl.formatMessage({ id: "dashboard.buttonHealthOkHint" });

      const sdLevel = row.sdStatus === "ERROR" ? "alert" : "ok";
      const sdLabel = (() => {
        switch (row.sdStatus) {
          case "OK":
            return intl.formatMessage({ id: "dashboard.sdOk" });
          case "ERROR":
            return row.sdRawStatus
              ? `${intl.formatMessage({ id: "dashboard.sdError" })} (${row.sdRawStatus})`
              : intl.formatMessage({ id: "dashboard.sdError" });
          case "UNKNOWN":
          case undefined:
          case null:
            return unknownLabel;
          default:
            return String(row.sdStatus);
        }
      })();

      const buttonLabel = buttonHealthIsBad
        ? intl.formatMessage({ id: "dashboard.buttonShortCircuit" })
        : intl.formatMessage({ id: "dashboard.buttonNoShort" });
      const buttonLevel = buttonHealthIsBad ? "alert" : "ok";

      return {
        id: `${row.establishmentId}:${row.deviceId}`,
        establishmentId: row.establishmentId,
        deviceId: row.deviceId,
        name: displayName,
        cameraStatus: online ? "online" : "offline",
        lastEvent,
        sdCardStatus: {
          label: sdLabel,
          level: sdLevel,
          adornment: (
            <span>
              <IconButton
                tooltip={intl.formatMessage({ id: "dashboard.sdFormat" })}
                aria-label={intl.formatMessage({ id: "dashboard.sdFormat" })}
                onClick={(e) => {
                  e.stopPropagation();
                  setSdFormatTarget({
                    establishmentId: row.establishmentId,
                    deviceId: row.deviceId,
                  });
                }}
                disabled={
                  !!formattingSdByDevice[`${row.establishmentId}:${row.deviceId}`]
                }
                sx={{ color: theme.palette.text.secondary }}
              >
                {formattingSdByDevice[
                  `${row.establishmentId}:${row.deviceId}`
                ] ? (
                  <CircularProgress size={18} />
                ) : (
                  <SdCardOutlined sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          ),
          tooltip: intl.formatMessage({ id: "dashboard.sdFormat" }),
        },
        buttonStatus: {
          label: buttonLabel,
          level: buttonLevel,
          adornment: buttonHealthIsBad ? (
            <NotificationsActiveOutlined
              sx={{ fontSize: 18, color: theme.palette.warning.main }}
            />
          ) : undefined,
          tooltip: buttonHealthTooltip,
        },
      } as CameraStatus;
    });
  }, [
    ALL_ESTABLISHMENTS,
    cameraRows,
    formattingSdByDevice,
    intl,
    formatLastEvent,
    selectedEstablishmentId,
    theme,
    unknownLabel,
  ]);

  useEffect(() => {
    if (!selectedEstablishmentId) return;
    const establishmentIds =
      selectedEstablishmentId === ALL_ESTABLISHMENTS
        ? undefined
        : [selectedEstablishmentId];
    refreshCameraStatus(false, establishmentIds);
  }, [ALL_ESTABLISHMENTS, refreshCameraStatus, selectedEstablishmentId]);

  useEffect(() => {
    let canceled = false;

    const loadEstablishments = async () => {
      setEstablishmentsLoading(true);
      try {
        // OWNER usa só os fallbacks leves (status das câmeras/templates). Os demais carregam
        // apenas a primeira página; o restante vem da busca server-side dos Autocompletes.
        const options = isOwner
          ? await fetchEstablishmentsForFilters({
              country,
              skipPrimaryEstablishmentsEndpoint: true,
            })
          : await searchEstablishmentsForFilters({ country });

        if (!canceled) {
          setEstablishmentsFromApi(
            options.map((option) => ({
              id: option.id,
              name: option.name,
            })),
          );
        }
      } catch {
        if (!canceled) {
          setEstablishmentsFromApi([]);
        }
      } finally {
        if (!canceled) {
          setEstablishmentsLoading(false);
        }
      }
    };

    loadEstablishments();
    return () => {
      canceled = true;
    };
  }, [country, isOwner]);

  useEffect(() => {
    if (
      !chartsEstablishmentId ||
      chartsEstablishmentId === ALL_ESTABLISHMENTS ||
      channelsByEstablishment.has(chartsEstablishmentId)
    ) {
      return;
    }

    let canceled = false;
    const establishmentId = chartsEstablishmentId;

    const loadEstablishmentChannels = async () => {
      try {
        const response = await api.get(
          `/app/establishments/${encodeURIComponent(establishmentId)}/channels`,
        );

        const channels = Array.isArray(response.data?.channels)
          ? response.data.channels
          : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

        const normalizedChannels = channels
          .map((channel: any) => {
            const id = String(channel?.id ?? channel?.channelId ?? "").trim();
            if (!id) return null;
            const name = String(channel?.name ?? channel?.channelName ?? id).trim();
            return { id, name: name || id };
          })
          .filter((item: TChannelOption | null): item is TChannelOption =>
            Boolean(item),
          )
          .sort((a: TChannelOption, b: TChannelOption) =>
            a.name.localeCompare(b.name, undefined, {
              sensitivity: "base",
            }),
          );

        if (!canceled) {
          setChannelsByEstablishment((prev) => {
            if (prev.has(establishmentId)) return prev;
            const next = new Map(prev);
            next.set(establishmentId, normalizedChannels);
            return next;
          });
        }
      } catch {
        if (!canceled) {
          setChannelsByEstablishment((prev) => {
            if (prev.has(establishmentId)) return prev;
            const next = new Map(prev);
            next.set(establishmentId, []);
            return next;
          });
        }
      }
    };

    loadEstablishmentChannels();
    return () => {
      canceled = true;
    };
  }, [ALL_ESTABLISHMENTS, chartsEstablishmentId, channelsByEstablishment]);

  useEffect(() => {
    if (chartsEstablishmentId === ALL_ESTABLISHMENTS) return;
    if (!channelsByEstablishment.has(chartsEstablishmentId)) return;
    if (chartsCourtId === ALL_COURTS) return;

    const channels = channelsByEstablishment.get(chartsEstablishmentId) ?? [];
    if (channels.some((channel) => channel.id === chartsCourtId)) return;
    setChartsCourtId(ALL_COURTS);
  }, [
    ALL_COURTS,
    ALL_ESTABLISHMENTS,
    channelsByEstablishment,
    chartsCourtId,
    chartsEstablishmentId,
  ]);

  useEffect(() => {
    if (!selectedEstablishmentId || selectedEstablishmentId === ALL_ESTABLISHMENTS) {
      return;
    }
    if (establishments.some((option) => option.id === selectedEstablishmentId)) {
      return;
    }
    if (!establishments.length) {
      setSelectedEstablishmentId("");
      return;
    }
    setSelectedEstablishmentId(establishments[0].id);
  }, [ALL_ESTABLISHMENTS, establishments, selectedEstablishmentId]);

  useEffect(() => {
    if (!establishments.length) {
      setChannelsByEstablishment(new Map());
      return;
    }

    const validIds = new Set(establishments.map((option) => option.id));
    setChannelsByEstablishment((prev) => {
      let changed = false;
      const next = new Map<string, TChannelOption[]>();
      prev.forEach((channels, id) => {
        if (!validIds.has(id)) {
          changed = true;
          return;
        }
        next.set(id, channels);
      });
      return changed ? next : prev;
    });
  }, [establishments]);

  useEffect(() => {
    // reset court filter when establishment changes (or when "Todos")
    setChartsCourtId(ALL_COURTS);
  }, [ALL_COURTS, chartsEstablishmentId]);

  // Evita "sumir" enquanto o authInfo ainda carrega.
  const showCharts = authInfo ? canOperate || isOwner : true;

  const offlineCamerasCount = useMemo(
    () => cameras.filter((c) => c.cameraStatus === "offline").length,
    [cameras],
  );
  const paymentPendingCount = useMemo(
    () => ownerRecentLives.filter((l) => l.paymentPending).length,
    [ownerRecentLives],
  );
  const activeLiveId = ownerStartedLives[0]?.liveId ?? null;
  const ownerMiniLives = useMemo(() => {
    const startedIds = new Set(ownerStartedLives.map((l) => l.liveId));
    return [...ownerStartedLives, ...ownerRecentLives.filter((l) => !startedIds.has(l.liveId))].slice(0, 5);
  }, [ownerStartedLives, ownerRecentLives]);

  const quickActions = useMemo(
    () => [
      {
        key: "createLive",
        label: intl.formatMessage({ id: "dashboard.quickActionCreateLive" }),
        icon: <AddCircleOutline />,
        onClick: () => navigate("/transmissoes-ao-vivo", { state: { openTab: "schedule" } }),
      },
      {
        key: "manageCameras",
        label: intl.formatMessage({ id: "dashboard.quickActionManageCameras" }),
        icon: <VideocamOutlined />,
        onClick: () => navigate("/cameras"),
      },
      {
        key: "configureLogos",
        label: intl.formatMessage({ id: "dashboard.quickActionConfigureLogos" }),
        icon: <PhotoLibraryOutlined />,
        onClick: () =>
          cameras.length === 1
            ? navigate(`/cameras/${cameras[0].establishmentId}/${cameras[0].id}/logos`)
            : navigate("/cameras"),
      },
      {
        key: "connectYoutube",
        label: intl.formatMessage({ id: "dashboard.quickActionConnectYoutube" }),
        icon: <YouTubeIcon />,
        onClick: () => {
          const popup = window.open("", "youtube-oauth", "width=520,height=650");
          setPendingYoutubeOAuthPopup(popup);
          navigate("/transmissoes-ao-vivo", { state: { autoConnectYoutube: true } });
        },
      },
      {
        key: "openScoreboard",
        label: intl.formatMessage({ id: "dashboard.quickActionScoreboard" }),
        icon: <ScoreboardOutlined />,
        onClick: () =>
          activeLiveId
            ? navigate(`/transmissoes-ao-vivo/live/${activeLiveId}?tab=placar`)
            : navigate("/transmissoes-ao-vivo"),
        disabled: !activeLiveId,
      },
    ],
    [activeLiveId, cameras, intl, navigate],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader title={intl.formatMessage({ id: canOperate ? "dashboard.adminTitle" : "dashboard.ownerProfileTitle" })} />

      {isOwner ? (
        <Grid container spacing={1.5}>
          {quickActions.map((action) => (
            <Grid item xs={6} sm={4} md={2.4} key={action.key}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <LoadingButton
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    py: 2,
                    px: 1,
                    borderRadius: "inherit",
                  }}
                >
                  {action.icon}
                  <Typography variant="caption" textAlign="center" sx={{ fontWeight: fontWeights.semiBold }}>
                    {action.label}
                  </Typography>
                </LoadingButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="overline" color="text.secondary">
            {intl.formatMessage({ id: "dashboard.sectionOperational" })}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <CameraStatusTable
            cameras={cameras}
            loading={cameraLoading}
            error={cameraError}
            headerRight={
              isOwnerSingleEstablishment ? null : (
                <Autocomplete
                  options={camerasEstablishmentOptions}
                  loading={establishmentsLoading || camerasEstablishmentSearch.loading}
                  filterOptions={isOwner ? undefined : serverSideFilter}
                  onOpen={camerasEstablishmentSearch.onOpen}
                  onClose={camerasEstablishmentSearch.onClose}
                  onInputChange={camerasEstablishmentSearch.onInputChange}
                  value={
                    camerasEstablishmentOptions.find(
                      (option) => option.id === selectedEstablishmentId,
                    ) ?? null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) => option.name}
                  onChange={(_, value) =>
                    setSelectedEstablishmentId(
                      String(value?.id ?? ALL_ESTABLISHMENTS),
                    )
                  }
                  sx={filterControlSx}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      label={intl.formatMessage({
                        id: "dashboard.establishmentLabelCameras",
                      })}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {establishmentsLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ marginY: 0 }}
                    />
                  )}
                />
              )
            }
          />
        </Grid>

        {isOwner ? (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="text.secondary">
                {intl.formatMessage({ id: "dashboard.sectionLives" })}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Card variant="outlined">
                <CardContent>
                  {ownerLivesLoading ? (
                    <Stack spacing={1}>
                      <Skeleton variant="rounded" height={32} />
                      <Skeleton variant="rounded" height={32} />
                      <Skeleton variant="rounded" height={32} />
                    </Stack>
                  ) : ownerMiniLives.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: "dashboard.noLivesToShow" })}
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {ownerMiniLives.map((live) => (
                        <ListItem
                          key={live.liveId}
                          disableGutters
                          divider
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/transmissoes-ao-vivo/live/${live.liveId}`)}
                          secondaryAction={
                            <StatusPill
                              size="small"
                              label={formatLiveStatus(intl, live.status)}
                              level={getLiveStatusLevel(live.status)}
                            />
                          }
                        >
                          <ListItemText primary={live.name} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="text.secondary">
                {intl.formatMessage({ id: "dashboard.sectionAlerts" })}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={1}>
                {offlineCamerasCount > 0 ? (
                  <Alert
                    severity="warning"
                    onClick={() => navigate("/cameras")}
                    sx={{ cursor: "pointer" }}
                  >
                    {intl.formatMessage(
                      { id: "dashboard.alertCamerasOffline" },
                      { count: offlineCamerasCount },
                    )}
                  </Alert>
                ) : null}
                {youtubeStatus?.requiresReconnect ? (
                  <Alert
                    severity="warning"
                    onClick={() => navigate("/transmissoes-ao-vivo")}
                    sx={{ cursor: "pointer" }}
                  >
                    {intl.formatMessage({ id: "dashboard.alertYoutubeReconnect" })}
                  </Alert>
                ) : null}
                {paymentPendingCount > 0 ? (
                  <Alert
                    severity="warning"
                    onClick={() => navigate("/transmissoes-ao-vivo")}
                    sx={{ cursor: "pointer" }}
                  >
                    {intl.formatMessage(
                      { id: "dashboard.alertPaymentPending" },
                      { count: paymentPendingCount },
                    )}
                  </Alert>
                ) : null}
                {offlineCamerasCount === 0 &&
                !youtubeStatus?.requiresReconnect &&
                paymentPendingCount === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "dashboard.noAlerts" })}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>
          </>
        ) : null}

        {showCharts ? (
          <>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={isOwner ? { cursor: "pointer" } : undefined}
                onClick={isOwner ? () => setOwnerMetricsExpanded((prev) => !prev) : undefined}
              >
                <Typography variant="overline" color="text.secondary">
                  {intl.formatMessage({ id: "dashboard.sectionMetrics" })}
                </Typography>
                {isOwner ? (
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      color: "text.secondary",
                      transform: ownerMetricsExpanded ? "rotate(180deg)" : "none",
                      transition: "transform 0.15s ease",
                    }}
                  />
                ) : null}
              </Stack>
              <Divider sx={{ mb: 1 }} />
            </Grid>
            {!isOwner || ownerMetricsExpanded ? (
              <>
            <Grid item xs={12}>
              <Box
                sx={{
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ mb: 1.5, color: "text.secondary" }}
                >
                  <FilterAltOutlined fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: fontWeights.semiBold }}>
                    {intl.formatMessage({ id: "dashboard.filtersLabel" })}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ md: "flex-start" }}
                >
                  <DatePickerField
                    label={intl.formatMessage({ id: "analyticsV2.startDate" })}
                    value={startDate}
                    onChange={setStartDate}
                    onToday={() => setStartDate(todayISO())}
                    onClear={() => setStartDate(last7DaysISO())}
                    minWidth={200}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <DatePickerField
                    label={intl.formatMessage({ id: "analyticsV2.endDate" })}
                    value={endDate}
                    onChange={setEndDate}
                    onToday={() => setEndDate(todayISO())}
                    onClear={() => setEndDate(todayISO())}
                    minWidth={200}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <Select
                    label={intl.formatMessage({ id: "analyticsV2.format" })}
                    helperText={intl.formatMessage({ id: "dashboard.formatHelper" })}
                    value={chartViewMode}
                    onChange={(e) =>
                      setChartViewMode(e.target.value as "chart" | "table")
                    }
                    MenuProps={{ sx: selectMenuSx, disablePortal: false } as any}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="chart">
                      {intl.formatMessage({ id: "analyticsV2.chart" })}
                    </MenuItem>
                    <MenuItem value="table">
                      {intl.formatMessage({ id: "analyticsV2.table" })}
                    </MenuItem>
                  </Select>
                  <Box
                    sx={{
                      width: "100%",
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        md:
                          isOwnerSingleEstablishment
                            ? "1fr"
                            : chartsEstablishmentId === ALL_ESTABLISHMENTS
                              ? "1fr"
                              : "1fr 1fr",
                      },
                    }}
                  >
                    {!isOwnerSingleEstablishment ? (
                      <Autocomplete
                        options={chartsEstablishmentOptions}
                        loading={establishmentsLoading || chartsEstablishmentSearch.loading}
                        filterOptions={isOwner ? undefined : serverSideFilter}
                        onOpen={chartsEstablishmentSearch.onOpen}
                        onClose={chartsEstablishmentSearch.onClose}
                        onInputChange={chartsEstablishmentSearch.onInputChange}
                        value={
                          chartsEstablishmentOptions.find(
                            (option) => option.id === chartsEstablishmentId,
                          ) ?? null
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(_, value) =>
                          setChartsEstablishmentId(
                            String(value?.id ?? ALL_ESTABLISHMENTS),
                          )
                        }
                        sx={{ ...filterControlSx, minWidth: 0, width: "100%" }}
                        renderInput={(params) => (
                          <Input
                            {...params}
                            label={intl.formatMessage({
                              id: "dashboard.establishmentLabelMetrics",
                            })}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {establishmentsLoading ? (
                                    <CircularProgress color="inherit" size={16} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{ marginY: 0 }}
                          />
                        )}
                      />
                    ) : null}

                    {chartsEstablishmentId !== ALL_ESTABLISHMENTS ? (
                      <Select
                        label={intl.formatMessage({ id: "analyticsV2.court" })}
                        value={chartsCourtId}
                        onChange={(event) =>
                          setChartsCourtId(String(event.target.value))
                        }
                        MenuProps={{ sx: selectMenuSx, disablePortal: false } as any}
                        sx={{ ...filterControlSx, minWidth: 0, width: "100%" }}
                      >
                        <MenuItem value={ALL_COURTS}>
                          {intl.formatMessage({ id: "analyticsV2.all" })}
                        </MenuItem>
                        {availableCourts.map((court) => (
                          <MenuItem key={court.id} value={court.id}>
                            {court.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : null}
                  </Box>
                  {canOperate ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      }}
                    >
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={locationFilters.states}
                        loading={locationFilters.statesLoading}
                        value={chartsStates}
                        onChange={(_, value) => {
                          setChartsStates(value as string[]);
                          setChartsCities([]);
                        }}
                        sx={{ ...filterControlSx, minWidth: 0, width: "100%" }}
                        renderInput={(params) => (
                          <Input
                            {...params}
                            label={intl.formatMessage({ id: "dashboard.stateFilterLabel" })}
                            sx={{ marginY: 0 }}
                          />
                        )}
                      />
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={locationFilters.cities}
                        loading={locationFilters.citiesLoading}
                        filterOptions={serverSideFilter}
                        value={chartsCities}
                        onChange={(_, value) => setChartsCities(value as string[])}
                        onOpen={locationFilters.onCitiesOpen}
                        onClose={locationFilters.onCitiesClose}
                        onInputChange={locationFilters.onCitiesInputChange}
                        sx={{ ...filterControlSx, minWidth: 0, width: "100%" }}
                        renderInput={(params) => (
                          <Input
                            {...params}
                            label={intl.formatMessage({ id: "dashboard.cityFilterLabel" })}
                            sx={{ marginY: 0 }}
                          />
                        )}
                      />
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            </Grid>

            {/* 1ª fileira: Vídeos Gerados e Visualizações */}
            <Grid item xs={12} md={6}>
              <MetricChartCard
                title={intl.formatMessage({ id: "dashboard.videos" })}
                metricName="video_statistics"
                granularity="daily"
                startDate={startDate}
                endDate={endDate}
                serieLabelGenerator={generateSerieLabel}
                aggregateTotal={canOperate}
                establishmentIds={chartEstablishmentIds}
                channelIds={chartChannelIds}
                cities={chartCities}
                states={chartStatesFilter}
                refreshKey={chartRefreshKey}
                showSeriesTable={false}
                darkMode={isDarkMode}
                height={320}
                cardSx={chartSurfaceStyles}
                displayMode={chartViewMode}
                allowEmptyScope
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricChartCard
                title={intl.formatMessage({ id: "dashboard.videoViews" })}
                metricName="custom_played_video_2"
                granularity="daily"
                startDate={startDate}
                endDate={endDate}
                serieLabelGenerator={generateSerieLabel}
                aggregateTotal={canOperate}
                establishmentIds={chartEstablishmentIds}
                channelIds={chartChannelIds}
                cities={chartCities}
                states={chartStatesFilter}
                refreshKey={chartRefreshKey}
                showSeriesTable={false}
                darkMode={isDarkMode}
                height={320}
                cardSx={chartSurfaceStyles}
                displayMode={chartViewMode}
                allowEmptyScope
              />
            </Grid>

            {/* 2ª fileira: Downloads e Impressões Projetadas (downloads * 0.6 * 500) */}
            <Grid item xs={12} md={6}>
              <MetricChartCard
                title={intl.formatMessage({ id: "dashboard.videoDownloads" })}
                metricName="custom_downloaded_video_2"
                granularity="daily"
                startDate={startDate}
                endDate={endDate}
                serieLabelGenerator={generateSerieLabel}
                aggregateTotal={canOperate}
                establishmentIds={chartEstablishmentIds}
                channelIds={chartChannelIds}
                cities={chartCities}
                states={chartStatesFilter}
                refreshKey={chartRefreshKey}
                showSeriesTable={false}
                darkMode={isDarkMode}
                height={320}
                cardSx={chartSurfaceStyles}
                displayMode={chartViewMode}
                allowEmptyScope
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricChartCard
                title={intl.formatMessage({
                  id: "dashboard.projectedImpressions",
                })}
                metricName="custom_downloaded_video_2"
                valueMultiplier={300}
                granularity="daily"
                startDate={startDate}
                endDate={endDate}
                serieLabelGenerator={generateSerieLabel}
                aggregateTotal={canOperate}
                establishmentIds={chartEstablishmentIds}
                channelIds={chartChannelIds}
                cities={chartCities}
                states={chartStatesFilter}
                refreshKey={chartRefreshKey}
                showSeriesTable={false}
                darkMode={isDarkMode}
                height={320}
                cardSx={chartSurfaceStyles}
                displayMode={chartViewMode}
                allowEmptyScope
              />
            </Grid>
              </>
            ) : null}
          </>
        ) : (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {intl.formatMessage({ id: "dashboard.metricsRestricted" })}
            </Typography>
          </Grid>
        )}
      </Grid>

      <ConfirmDialog
        open={!!sdFormatTarget}
        title={intl.formatMessage({ id: "dashboard.sdFormat" })}
        message={intl.formatMessage({ id: "dashboard.sdFormatConfirm" })}
        confirmLabel={intl.formatMessage({ id: "common.confirm" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onClose={() => setSdFormatTarget(null)}
        onConfirm={() => {
          if (!sdFormatTarget) return;
          const { establishmentId, deviceId } = sdFormatTarget;
          setSdFormatTarget(null);
          handleFormatSd(establishmentId, deviceId);
        }}
      />
    </Box>
  );
};
export default Dashboard;
